import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Box, Button, LinearProgress, Paper, TextField, Typography } from '@mui/material';

import { nanoid } from 'nanoid';

import { currency, enqueueSnackbarError, enqueueSnackbarSuccess } from 'lib/helpers';
import { NfcCardBalance, addCardBalance, isRefusedByApi, resetCardBalance } from 'lib/models/nfcCards';

import useNfcCardLabels from 'hooks/useNfcCardLabels';

import ConfirmationDialog from 'components/ConfirmationDialog';

type Props = {
	cardId: string;
	balance: NfcCardBalance | null;
	loading: boolean;
	failed: boolean;
	/** The balance a load or a reset answered with. */
	onBalance: (balance: NfcCardBalance) => void;
	/** Called after a load or a reset was sent, whatever came of it, so the movements are read again. */
	onChanged: () => void;
};

/** The key of one load. The API takes letters, digits, `_` and `-`, from 8 to 128 of them. */
const newOperationId = () => `nfc-balance-${nanoid()}`;

/**
 * The amount typed, as a number the API would load, or null.
 *
 * @param value What was typed, with a comma or a dot as the decimal separator
 */
function readAmount(value: string): number | null {
	const trimmed = value.trim();
	if (!trimmed) return null;

	const amount = Number(trimmed.replace(',', '.'));

	return Number.isFinite(amount) && amount > 0 ? amount : null;
}

/**
 * The balance of a card: what it holds, a load and a reset to zero. Same job as the
 * `UserBalance` panel of the users table, spoken by the card's id.
 *
 * A load is idempotent by the `operationId` drawn here. The key is drawn once per load and
 * only replaced when the load surely happened (the API answered it) or surely did not (the
 * API refused the first send of it). When the answer never arrived, the load may have reached
 * the balance anyway, so the key and the amount are kept and the retry sends the same pair: the
 * API answers the load it already did instead of loading twice. A double click sends once, and
 * would be answered as a retry if it got through.
 */
export default function NfcCardBalancePanel({ cardId, balance, loading, failed, onBalance, onChanged }: Props) {
	const { t } = useTranslation();
	const { cardName } = useNfcCardLabels();

	const [draft, setDraft] = useState('');
	const [operationId, setOperationId] = useState(newOperationId);
	// The amount of a load whose answer never arrived. While there is one, the amount is locked
	// to it, because the API refuses the same key with another amount.
	const [pendingAmount, setPendingAmount] = useState<number | null>(null);

	const [saving, setSaving] = useState(false);
	// Set in the same tick as the click, before the re-render that disables the button
	const sending = useRef(false);

	const [showResetConfirmation, setShowResetConfirmation] = useState(false);
	const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);

	const amount = pendingAmount ?? readAmount(draft);
	const brokenAmount = pendingAmount === null && !!draft.trim() && readAmount(draft) === null;

	const startNewLoad = () => {
		setOperationId(newOperationId());
		setPendingAmount(null);
	};

	const onLoad = async () => {
		if (sending.current || amount === null) return;

		sending.current = true;
		setSaving(true);

		try {
			const result = await addCardBalance(cardId, amount, operationId);
			onBalance(result.balance);
			enqueueSnackbarSuccess(
				result.created ? t('nfc:balanceLoaded', { amount: currency(amount) }) : t('nfc:balanceAlreadyLoaded')
			);
			startNewLoad();
			setDraft('');
		} catch (error) {
			enqueueSnackbarError(error);
			// A refused first send never loaded, so the next one is another load. A refused retry
			// says nothing of the send before it, which may have loaded: the key is kept.
			if (isRefusedByApi(error) && pendingAmount === null) {
				startNewLoad();
			} else {
				setPendingAmount(amount);
			}
		}

		sending.current = false;
		setSaving(false);
		onChanged();
	};

	const onResetHandler = async (mustReset: boolean) => {
		setShowResetConfirmation(false);

		if (!mustReset) return;

		setSaving(true);
		try {
			const { resetAmount, ...result } = await resetCardBalance(cardId);
			onBalance(result);
			enqueueSnackbarSuccess(t('nfc:balanceResetDone', { amount: currency(resetAmount) }));
		} catch (error) {
			enqueueSnackbarError(error);
		}
		setSaving(false);
		onChanged();
	};

	const onDiscardHandler = (mustDiscard: boolean) => {
		setShowDiscardConfirmation(false);
		if (mustDiscard) startNewLoad();
	};

	// The API refuses to load a blocked or expired card; the button only spares the attempt
	const canLoad = !!balance?.usable && amount !== null && !saving;

	return (
		<Paper variant="outlined" sx={{ padding: 2, marginBottom: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
			{showResetConfirmation && !!balance && (
				<ConfirmationDialog
					title={t('nfc:balanceResetTitle')}
					description={t('nfc:balanceResetDescription', {
						card: cardName(balance.label),
						balance: currency(balance.balance),
					})}
					confirmButton={t('nfc:balanceReset')}
					onClose={onResetHandler}
				/>
			)}

			{showDiscardConfirmation && pendingAmount !== null && (
				<ConfirmationDialog
					title={t('nfc:balanceDiscardTitle')}
					description={t('nfc:balanceDiscardDescription', { amount: currency(pendingAmount) })}
					confirmButton={t('nfc:balanceDiscard')}
					onClose={onDiscardHandler}
				/>
			)}

			{(loading || saving) && <LinearProgress />}

			{failed && !balance && <Alert severity="error">{t('nfc:balanceFailed')}</Alert>}

			{!!balance && (
				<Typography variant="h5">
					{t('nfc:balanceCurrent')}: {currency(balance.balance)}
				</Typography>
			)}

			{!!balance && !balance.usable && <Alert severity="info">{t('nfc:balanceUnusable')}</Alert>}

			{pendingAmount !== null && (
				<Alert
					severity="warning"
					action={
						<Button color="inherit" size="small" onClick={() => setShowDiscardConfirmation(true)} disabled={saving}>
							{t('nfc:balanceDiscardAttempt')}
						</Button>
					}
				>
					{t('nfc:balancePendingAttempt')}
				</Alert>
			)}

			<Box
				component="form"
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					flexWrap: 'wrap',
					gap: 2,
					alignItems: { xs: 'stretch', sm: 'flex-start' },
				}}
				onSubmit={(event) => {
					event.preventDefault();
					if (canLoad) onLoad();
				}}
			>
				<TextField
					value={pendingAmount !== null ? String(pendingAmount) : draft}
					label={t('nfc:balanceAmount')}
					onChange={(event) => setDraft(event.target.value)}
					disabled={!balance || pendingAmount !== null || saving}
					error={brokenAmount}
					helperText={brokenAmount ? t('nfc:balanceAmountInvalid') : undefined}
					inputProps={{ inputMode: 'decimal' }}
				/>

				<Button type="submit" variant="contained" size="large" disabled={!canLoad} sx={{ marginTop: { sm: 0.5 } }}>
					{t('nfc:balanceLoad')}
				</Button>

				<Button
					variant="outlined"
					color="warning"
					size="large"
					onClick={() => setShowResetConfirmation(true)}
					disabled={!balance || saving}
					sx={{ marginTop: { sm: 0.5 } }}
				>
					{t('nfc:balanceReset')}
				</Button>
			</Box>
		</Paper>
	);
}
